package game

import com.soywiz.kds.iterators.fastForEach
import com.soywiz.korau.sound.Sound
import com.soywiz.korge.view.Views
import com.soywiz.korui.layout.MathEx.max
import game.entity.Direction
import view.Terminal
import kotlin.random.Random

enum class LightSource(val radius: Int) {
    None(0),
    Candle(2),
    Torch(3),
    Lantern(5),
}

enum class Jump(val distance: Int) {
    None(0),
    Spring(1),
    Cape(2),
}

enum class Weapon(val damage: Int, val length: Int) {
    None(0, 0),
    Stick(1, 1),
    Sword(2, 1),
    RoyalSword(4, 2),
}

enum class Armor(val reduction: Int) {
    None(1),
    Light(2),
    Holy(4)
}

enum class SpeedUp(val speed: Int) {
    None(10),
    BlueFeather(7),
    RedFeather(4)
}

enum class Bracer(val strength: Int) {
    None(0),
    Cloth(1),
    Dragon(2),
}

class PlayerInput {
    var up = false
    var down = false
    var left = false
    var right = false
    var hit = false
}

class Player {
    var life = 3
    var maxLife = 3 // 22 max
    val fullHealth
        get() = life == maxLife
    var coolDown = 0
    private var attackCoolDown = 0
    val moving: Boolean
        get() = colVel != 0 || rowVel != 0
    var col = 0
    var row = 0
    var colVel = 0
    var rowVel = 0
    var direction = Direction.South
    var attacking = false
    var sliding = false
    var slidingColVel = 0
    var slidingRowVel = 0
    private lateinit var stepSounds: List<Sound>

    private lateinit var damageSound: Sound
    private lateinit var deathSound: Sound
    private lateinit var views: Views
    lateinit var attackSprite: CharSprite
    lateinit var attackSprites: Map<Weapon, Map<Direction, CharSprite>>
    lateinit var attackSounds: Map<Weapon, Sound>

    // Items
    var lens = false
    var map = false
    var shardScanner = false
    var itemScanner = false
    var loreScanner = false
    var flippers = false
    var lightSource = LightSource.None
    var bracer = Bracer.None
    var speed = SpeedUp.None
    var jump = Jump.None
    var weapon = Weapon.None
    var armor = Armor.None
    var lavaBoots = false
    var divineBoots = false

    private var invincible = 0
    private var slideSpeed = 3
    private val invincibleTimer = 100

    fun render(terminal: Terminal, colOffset: Int, rowOffset: Int) {
        if (invincible == 0 || invincible / 5 % 2 == 0) {
            with(terminal.getChar(col + colOffset, row + rowOffset)) {
                char = 2
                bgColor = fgColor  // Make fgColor the background--blends better!
                fgColor = CgaColor.LIGHT_BLUE.rgba
            }
        }

        if (attacking && weapon != Weapon.None && attackCoolDown > 10) {
            attackSprites[weapon]?.get(direction)?.render(terminal, col + colOffset, row + rowOffset, 0)
        }
    }

    fun takeDamage(damage: Int) {
        if (invincible == 0) {
            life = max(life - max(damage / armor.reduction, 1), 0)

            if (life == 0) {
                deathSound.play(views.coroutineContext)
            } else {
                invincible = invincibleTimer

                damageSound.play(views.coroutineContext)
            }
        }
    }

    fun init(game: Game) {
        stepSounds = listOf(
            game.getSound("step-1"),
            game.getSound("step-2"),
            game.getSound("step-3"),
        )

        damageSound = game.getSound("damage")
        views = game.views

        deathSound = game.getSound("death")

        attackSprite = game.getSpriteById("player")

        attackSprites = mapOf(
            Weapon.Stick to mapOf(
                Direction.North to game.getSpriteById("player-weapon-1-north"),
                Direction.West to game.getSpriteById("player-weapon-1-west"),
                Direction.South to game.getSpriteById("player-weapon-1-south"),
                Direction.East to game.getSpriteById("player-weapon-1-east"),
            ),
            Weapon.Sword to mapOf(
                Direction.North to game.getSpriteById("player-weapon-2-north"),
                Direction.West to game.getSpriteById("player-weapon-2-west"),
                Direction.South to game.getSpriteById("player-weapon-2-south"),
                Direction.East to game.getSpriteById("player-weapon-2-east"),
            ),
            Weapon.RoyalSword to mapOf(
                Direction.North to game.getSpriteById("player-weapon-3-north"),
                Direction.West to game.getSpriteById("player-weapon-3-west"),
                Direction.South to game.getSpriteById("player-weapon-3-south"),
                Direction.East to game.getSpriteById("player-weapon-3-east"),
            )
        )

        attackSounds = mapOf(
            Weapon.Stick to game.getSound("attack-1"),
            Weapon.Sword to game.getSound("attack-2"),
            Weapon.RoyalSword to game.getSound("attack-3"),
        )
    }

    fun reset(game: Game) {
        life = 3
        maxLife = 3
        lens = false
        map = false
        shardScanner = false
        itemScanner = false
        loreScanner = false
        flippers = false
        weapon = Weapon.None
        armor = Armor.None
        lightSource = LightSource.None
        bracer = Bracer.None
        speed = SpeedUp.None
        jump = Jump.None
        lavaBoots = false
        divineBoots = false
    }

    fun tick(game: Game, input: PlayerInput) {
        if (invincible > 0) {
            invincible--
        }

        if (divineBoots && game.currentRoom[col, row].type == TileType.Ice) {
            when {
                input.left -> {
                    slidingColVel = -1
                    slidingRowVel = 0
                }
                input.right -> {
                    slidingColVel = 1
                    slidingRowVel = 0
                }
                input.up -> {
                    slidingColVel = 0
                    slidingRowVel = -1
                }
                input.down -> {
                    slidingColVel = 0
                    slidingRowVel = 1
                }
            }
        }

        colVel = when {
            sliding -> slidingColVel
            input.left -> -1
            input.right -> 1
            else -> 0
        }

        rowVel = when {
            colVel != 0 -> 0  // Already moving horizontally
            sliding -> slidingRowVel
            input.up -> -1
            input.down -> 1
            else -> 0
        }

        direction = when {
            colVel == +1 -> Direction.East
            colVel == -1 -> Direction.West
            rowVel == +1 -> Direction.South
            rowVel == -1 -> Direction.North
            else -> direction
        }

        if (game.currentRoom[col, row].type == TileType.Lava) {
            if (!lavaBoots) {
                takeLavaDamage(game)
            }
        }

        when {
            weapon == Weapon.None -> {
                // Skip!
            }
            attackCoolDown != 0 -> {
                attackCoolDown--
            }
            attackCoolDown == 0 && input.hit -> {
                attacking = true
                attackCoolDown = 20

                val (deltaCol, deltaRow) = when (direction) {
                    Direction.North -> Pair(0, -1)
                    Direction.South -> Pair(0, 1)
                    Direction.East -> Pair(1, 0)
                    Direction.West -> Pair(-1, 0)
                }

                for (i in 1..weapon.length) {
                    game.currentRoom.entities.filter {
                        it.contains(col + deltaCol * i, row + deltaRow * i)
//                        col == col + (deltaCol * i) && it.row == row + (deltaRow * i)
                    }.fastForEach {
                        it.onAttacked(game, weapon)
                    }
                }

                attackSounds.getValue(weapon).play(views.coroutineContext)
            }
            else -> {
                attacking = false
            }
        }

        when {
            coolDown != 0 -> {
                coolDown--
            }
            moving || sliding -> {
                val desiredCol = col + colVel
                val desiredRow = row + rowVel

                // Are we going off-screen?
                when {
                    desiredCol < 0 -> {
                        // Change room
                        game.startScrollTransition(game.currentRoom.exitWest, Direction.West)
                        col = ROOM_WIDTH - 1
                    }
                    desiredCol >= ROOM_WIDTH -> {
                        // Change room
                        game.startScrollTransition(game.currentRoom.exitEast, Direction.East)
                        col = 0
                    }
                    desiredRow < 0 -> {
                        // Change room
                        game.startScrollTransition(game.currentRoom.exitNorth, Direction.North)
                        row = ROOM_HEIGHT - 1
                    }
                    desiredRow >= ROOM_HEIGHT -> {
                        // Change room
                        game.startScrollTransition(game.currentRoom.exitSouth, Direction.South)
                        row = 0
                    }
                    else -> {
                        attemptMove(game, desiredCol, desiredRow)
                    }
                }
                coolDown = if (sliding) slideSpeed else speed.speed
            }
            else -> {
                // idle
            }
        }
    }

    private fun playStepSound(game: Game) {
        if (!game.settings.muteSteps) {
            game.playSound(stepSounds[Random.nextInt(stepSounds.size)])
        }
    }

    fun attemptMove(game: Game, desiredCol: Int, desiredRow: Int) {
        if (game.currentRoom.dark && game.player.lightSource == LightSource.None) {
            game.showDialog("Dialog_Dark")
        } else {
            val tile = game.currentRoom[desiredCol, desiredRow]

            when (tile.type) {
                TileType.Floor -> moveFloor(game, desiredCol, desiredRow)
                TileType.Wall -> moveWall(game, desiredCol, desiredRow)
                TileType.Hole -> moveHole(game, desiredCol, desiredRow)
                TileType.Water -> moveWater(game, desiredCol, desiredRow)
                TileType.Ice -> moveIce(game, desiredCol, desiredRow)
                TileType.Lava -> moveLava(game, desiredCol, desiredRow)
            }
        }
    }

    private fun moveFloor(game: Game, desiredCol: Int, desiredRow: Int) {
        playStepSound(game)

        doMove(game, desiredCol, desiredRow)
    }

    fun doMove(game: Game, desiredCol: Int, desiredRow: Int) {
        game.player.sliding = false

        val canMove = checkCollisionWithEntities(game, desiredCol, desiredRow)

        if (canMove) {
            game.player.col = desiredCol
            game.player.row = desiredRow

        }
    }

    private fun checkCollisionWithEntities(
        game: Game,
        desiredCol: Int,
        desiredRow: Int
    ) = game.currentRoom.entities.filter {
        it.intersects(desiredCol, desiredRow, 1, 1)
    }.all {
        it.onCollideWithPlayer(game, game.currentRoom)
    }

    private fun moveWall(game: Game, desiredCol: Int, desiredRow: Int) {
        sliding = false

//        val tile = game.currentRoom[desiredCol, desiredRow]
//
//        val message = when (tile.char) {
//            0x5, 0x6 -> game.text("Message_Wall_Tree")
//            0x1E -> game.text("Message_Wall_Boulder")
//            in 0xB0..0xB2 -> game.text("Message_Wall_StoneWall")
//            0xE8 -> game.text("Message_Wall_Column")
//            in 0xB3..0xDA -> game.text("Message_Wall_Wall")
//            0xF1, 0xC5, 0xD8 -> game.text("Message_Wall_Gravestone")
//            else -> game.text("Message_Wall_Generic")
//        }
//
//        game.message = message
    }

    private fun moveHole(game: Game, desiredCol: Int, desiredRow: Int) {
        if (sliding) {
            // Already jumping
            col = desiredCol
            row = desiredRow

            return
        }

        var canJump = false

        for (distance in 0..jump.distance) {
            val jumpCol = desiredCol + (distance * colVel)
            val jumpRow = desiredRow + (distance * rowVel)
            val tile = game.currentRoom[jumpCol, jumpRow]
            if ((tile.type == TileType.Floor || tile.type == TileType.Lava)) {
                canJump = true
                break
            }
        }

        if (canJump) {
            sliding = true
            slidingColVel = colVel
            slidingRowVel = rowVel

            col = desiredCol
            row = desiredRow

            game.playSound("jump")
        } else {
            game.message = game.text("Message_Jump")
        }
    }

    private fun moveWater(game: Game, desiredCol: Int, desiredRow: Int) {
        if (flippers) {
            if (game.currentRoom[col, row].type == TileType.Floor) {
                game.playSound("splash")
            } else {
                game.playSound("swim")
            }
            doMove(game, desiredCol, desiredRow)
        } else {
            game.message = game.text("Message_Swim")
        }
    }

    private fun moveIce(game: Game, desiredCol: Int, desiredRow: Int) {
        if (checkCollisionWithEntities(game, desiredCol, desiredRow)) {
            val tileType = game.currentRoom[col, row].type
            if (!sliding) {
                game.playSound("slip")
            }
            sliding = true

            slidingColVel = colVel
            slidingRowVel = rowVel

            col += colVel
            row += rowVel
        } else {
            sliding = false
        }

        coolDown = slideSpeed
    }

    private fun moveLava(game: Game, desiredCol: Int, desiredRow: Int) {
        doMove(game, desiredCol, desiredRow)

        if (!lavaBoots) {
            takeLavaDamage(game)
        }

        game.playSound("lava")
    }

    private fun takeLavaDamage(game: Game) {
        takeDamage(1)

        game.message = game.text("Message_Lava")
    }

    fun heal() {
        life = maxLife
    }
}
